import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { MAX_FOLDER_DEPTH } from '../../../../constants/folder';
import { SidebarMoveService } from '../../../../service/SidebarMoveService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useSidebarTreeStore } from '../../../../store/storeSidebarTree';
import { NeighborRanks, computeNeighbors } from './computeNeighbors';
import { canDrop } from './dropRules';
import { getSubtreeMaxDepthIndexed, wouldExceedDepth } from './folderDepth';
import {
  MoveTarget,
  activeIdOf,
  nodeKindOf,
  normalizeBackendKind,
  resolveTarget,
} from './moveTarget';
import { enqueue } from './mutationQueue';
import { applyOptimisticMove } from './optimisticCache';
import { RankExhausted, rankBetween } from './rank';
import type { DragData, DropData, DropPosition } from './types';

export interface MoveArgs {
  active: DragData;
  over: DropData;
  position: DropPosition | null;
}

export type PerformMove = (args: MoveArgs) => Promise<void>;

const refreshSidebar = () =>
  window.dispatchEvent(new CustomEvent('sidebar-refresh'));

export const useMoveNode = () => {
  const setNotification = useNotificationStore(s => s.setNotification);
  const queryClient = useQueryClient();

  const notifyError = useCallback(
    (message: string) =>
      setNotification({ type: NotificationType.Error, message }),
    [setNotification],
  );

  const refreshTree = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['sidebar'] });
  }, [queryClient]);

  const computeRankWithRebalance = useCallback(
    async (
      target: MoveTarget,
      neighbors: NeighborRanks,
      nodeId: string,
      over: DropData,
      position: DropPosition | null,
    ): Promise<string | null> => {
      try {
        return rankBetween(neighbors.prev, neighbors.next);
      } catch (err) {
        if (!(err instanceof RankExhausted)) {
          notifyError(err instanceof Error ? err.message : 'Invalid drop');
          return null;
        }
      }
      try {
        await SidebarMoveService.rebalanceSiblings({
          parentId: target.parentId,
          section: target.section,
          spaceId: target.spaceId,
        });
        refreshTree();
        const refreshed = computeNeighbors(
          target.parentId,
          target.section,
          target.spaceId,
          nodeId,
          over,
          position,
        );
        return rankBetween(refreshed.prev, refreshed.next);
      } catch (err) {
        notifyError(
          err instanceof Error ? err.message : 'Failed to rebalance siblings',
        );
        return null;
      }
    },
    [notifyError, refreshTree],
  );

  const performMove = useCallback(
    async ({ active, over, position }: MoveArgs) => {
      const decision = canDrop(active, over, position);
      if (!decision.ok) {
        if (decision.reason) notifyError(decision.reason);
        return;
      }

      const target = resolveTarget(over, active, position);
      if (!target) return;

      const nodeId = activeIdOf(active);
      const store = useSidebarTreeStore.getState();
      const activeNode = store.nodes[nodeId];
      if (!activeNode) {
        notifyError('Sidebar item not loaded yet, please refresh.');
        return;
      }

      if (activeNode.spaceId !== target.spaceId) return;

      if (target.parentId !== null) {
        const parentNode = store.nodes[target.parentId];
        if (!parentNode) {
          notifyError('Destination folder no longer exists.');
          return;
        }
        if (parentNode.spaceId !== target.spaceId) return;
        if (parentNode.section !== target.section) return;
        if (parentNode.kind !== 'folder') return;
      }

      if (activeNode.kind === 'folder') {
        const subtreeDepth = getSubtreeMaxDepthIndexed(
          store.childrenByParent,
          nodeId,
        );
        if (wouldExceedDepth(store.nodes, target.parentId, subtreeDepth)) {
          notifyError(
            `Folder depth limit reached (max ${MAX_FOLDER_DEPTH} levels).`,
          );
          return;
        }
      }

      const neighbors = computeNeighbors(
        target.parentId,
        target.section,
        target.spaceId,
        nodeId,
        over,
        position,
      );
      const rank = await computeRankWithRebalance(
        target,
        neighbors,
        nodeId,
        over,
        position,
      );
      if (rank === null) return;

      const snapshot = store.snapshot(nodeId);
      if (!snapshot) return;
      const from = {
        parentId: snapshot.node.parentId,
        spaceId: snapshot.node.spaceId,
        section: snapshot.node.section,
      };
      const version = store.applyMove(nodeId, {
        parentId: target.parentId,
        rank,
        section: target.section,
        spaceId: target.spaceId,
      });
      if (version === null) return;

      const backendKind = normalizeBackendKind(nodeKindOf(active));
      applyOptimisticMove(queryClient, {
        nodeId,
        nodeKind: backendKind,
        from,
        to: {
          parentId: target.parentId,
          spaceId: target.spaceId,
          section: target.section,
        },
        rank,
      });

      enqueue(nodeId, {
        run: async () => {
          try {
            const res = await SidebarMoveService.moveNode({
              nodeId,
              nodeKind: backendKind,
              parentId: target.parentId,
              rank,
              section: target.parentId ? undefined : target.section,
              spaceId: target.parentId ? undefined : target.spaceId,
            });
            if (res?.node) {
              useSidebarTreeStore.getState().patchNode(res.node, version);
            }
          } catch (apiErr) {
            const current = useSidebarTreeStore.getState().nodes[nodeId];
            if (current?.localVersion === version) {
              useSidebarTreeStore
                .getState()
                .revertMove(nodeId, snapshot, version);
              notifyError(
                apiErr instanceof Error
                  ? apiErr.message
                  : 'Failed to move item',
              );
            }
            refreshSidebar();
          }
        },
      });
    },
    [computeRankWithRebalance, notifyError, queryClient],
  );

  return { performMove };
};
